import { ArrowRightOutlined } from '@ant-design/icons';
import {
  Card,
  Col,
  Divider,
  Row,
  Skeleton,
  Space,
  Tabs,
  Typography,
} from 'antd';
import Accordion from 'components/Accordion';
import TagComponent from 'components/Tags';
import TaskInfo from 'components/TaskInfo';
import * as dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { routes } from 'routes/AllRoutes';
import useFarmersByGroup from 'screens/farmer-groups/hooks/useFarmersByGroupQuery';
import { getStatusTagColor } from 'screens/farmer/helper';
import { FarmerGroupV2 } from 'types/response';
import {
  capitalizeWords,
  getStakeholderTypeForCountry,
  isMobileScreenSize,
  renderDate,
} from 'utils/helpers';

const renderEstimateDate = (str?: string) => {
  if (!str) return '-';
  const day = Number(str.split(' ')?.[0]);
  const month = Number(str.split(' ')?.[1]);
  const dayjsValue = dayjs()
    .set('date', day)
    .set('month', month - 1);
  return dayjsValue.format('DD MMMM');
};

const FarmerGroupContent = ({
  country,
  data,
  activeKey,
  setActiveKey,
}: {
  country: string;
  data?: FarmerGroupV2;
  activeKey: string;
  setActiveKey: (key: string) => void;
}) => {
  const { t } = useTranslation();
  const { data: farmersData, isLoading } = useFarmersByGroup(data?.id);
  const tabItems = [
    {
      key: '1',
      label: t('addFgForm.personalDetails'),
      children: (
        <Row style={{ marginTop: 8 }} gutter={[16, 16]}>
          <Col span={12}>
            <TaskInfo
              wrapperClass="no-padding"
              header={t('addFgForm.farmerGroupName')}
              value={data?.name}
            />
          </Col>
          <Col span={12}>
            <TaskInfo
              wrapperClass="no-padding"
              header={getStakeholderTypeForCountry(country, 'PB')}
              value={data?.paddyBuyer?.name}
            />
          </Col>
          <Col span={12}>
            <TaskInfo
              wrapperClass="no-padding"
              header={t('addFgForm.farmerGroupLeader')}
              value={data?.groupLeader?.name}
            />
          </Col>
          <Col span={12}>
            <TaskInfo
              wrapperClass="no-padding"
              header={t('addFgForm.createdBy')}
              value={data?.createdByEmail}
            />
          </Col>
          <Col span={12}>
            <TaskInfo
              wrapperClass="no-padding"
              header={t('addFgForm.totalFarmers')}
              value={data?.totalFarmers}
            />
            <Link
              to={{
                pathname: routes.farmers,
                search: `groupId=${data?.id}&groupName=${data?.name}`,
              }}
            >
              {t('addFgForm.viewFarmers')} <ArrowRightOutlined />
            </Link>
          </Col>
          <Col span={12}>
            <TaskInfo
              wrapperClass="no-padding"
              header={t('addFgForm.ownedHectares')}
              value={data?.cubeOwnedHectares}
            />
          </Col>

          <Col span={12}>
            <TaskInfo
              wrapperClass="no-padding"
              header={t('addFgForm.joiningFSS')}
              value={data?.fullstackParticipant ? 'Yes' : 'No'}
            />
          </Col>

          <Divider className="no-padding" style={{ margin: '12px 0' }} />
          <Col span={24}>
            <TaskInfo
              wrapperClass="no-padding"
              header={t('addFgForm.address')}
              value={data?.address?.address}
            />
          </Col>
          <Col span={12}>
            <TaskInfo
              wrapperClass="no-padding"
              header={t('addFgForm.province')}
              value={data?.address?.stateOrProvince}
            />
          </Col>
          <Col span={12}>
            <TaskInfo
              wrapperClass="no-padding"
              header={t('addFgForm.district')}
              value={data?.address?.district}
            />
          </Col>
          <Col span={12}>
            <TaskInfo
              wrapperClass="no-padding"
              header={t('addFgForm.subDistrict')}
              value={data?.address?.subDistrict}
            />
          </Col>
          <Col span={12}>
            <TaskInfo
              wrapperClass="no-padding"
              header={t('addFgForm.village')}
              value={data?.address?.cityOrVillage}
            />
          </Col>
          <Divider style={{ margin: '12px 0' }} />
          <Col span={12}>
            <TaskInfo
              wrapperClass="no-padding"
              header={t('addFgForm.currentJuniorAgronomist')}
              value={
                data?.rizeusersByFieldCoordinator?.email ??
                data?.cubeAgAuditList?.[0]?.agronomistEmail
              }
            />
          </Col>
          <Col span={12}>
            <TaskInfo
              wrapperClass="no-padding"
              header={t('addFgForm.dateAssigned')}
              value={
                data?.cubeAgAuditList?.length
                  ? renderDate(
                      data?.cubeAgAuditList?.[0]?.lastModifiedDate,
                      '-',
                      'DD-MM-YYYY',
                    )
                  : renderDate(data?.createdAt)
              }
            />
          </Col>
          <Col span={12}>
            <TaskInfo
              wrapperClass="no-padding"
              header={t('addFgForm.assignedBy')}
              value={
                data?.cubeAgAuditList?.length
                  ? data?.cubeAgAuditList?.[0]?.createdBy
                  : data?.createdByEmail
              }
            />
          </Col>
          {!!data?.cubeAgAuditList?.slice(1)?.length && (
            <Col span={24}>
              <Accordion title={t('addFgForm.history')}>
                {data?.cubeAgAuditList
                  ?.slice(1)
                  ?.map((historyData, index, arr) => (
                    <Row key={`audit-history-${index}`} gutter={[16, 16]}>
                      <Col span={isMobileScreenSize ? 24 : 12}>
                        <TaskInfo
                          wrapperClass="no-padding"
                          header={
                            historyData?.agronomistEmail
                              ? t('addFgForm.juniorAgronomist')
                              : t('common.filters.awdAgronomist')
                          }
                          value={
                            historyData?.agronomistEmail ??
                            historyData?.almAgronomistEmail
                          }
                        />
                      </Col>
                      <Col span={12}>
                        <TaskInfo
                          wrapperClass="no-padding"
                          header={t('addFgForm.dateAssigned')}
                          value={renderDate(
                            historyData?.reportedDate,
                            '-',
                            'DD-MM-YYYY',
                          )}
                        />
                      </Col>
                      <Col span={12}>
                        <TaskInfo
                          wrapperClass="no-padding"
                          header={t('addFgForm.assignedBy')}
                          value={historyData?.createdBy}
                        />
                      </Col>
                      {index < arr.length - 1 && (
                        <Divider style={{ margin: '12px 0 24px 0' }} />
                      )}
                    </Row>
                  ))}
              </Accordion>
            </Col>
          )}
        </Row>
      ),
    },
    {
      key: '2',
      label: t('addFgForm.farmers'),
      children: (
        <Space direction="vertical" style={{ display: 'flex' }}>
          {isLoading && (
            <Card className="farmer-card">
              <Skeleton active title={false} />
            </Card>
          )}
          {farmersData?.data?.map((farmer) => (
            <Card className="farmer-card" key={`farmers-${farmer.id}`}>
              <Typography.Title level={5}>{farmer?.name}</Typography.Title>
              <TagComponent
                label={capitalizeWords(farmer?.status)}
                color={getStatusTagColor(farmer?.status)}
              />
            </Card>
          ))}
        </Space>
      ),
    },
    {
      key: '3',
      label: t('addFgForm.plantingDates'),
      children: (
        <Space direction="vertical" size={12} style={{ display: 'flex' }}>
          <Card className="farmer-card">
            <Row>
              <Col span={24}>
                <Typography.Title level={5}>
                  {t('addFgForm.plantingSeason1')}
                </Typography.Title>
              </Col>
              <Col span={12}>
                <TaskInfo
                  wrapperClass="no-padding"
                  header={t('addFgForm.estimatedPlantingDate')}
                  value={renderEstimateDate(data?.firstEstimatedStartDate)}
                />
              </Col>
              <Col span={12}>
                <TaskInfo
                  wrapperClass="no-padding"
                  header={t('addFgForm.estimatedHarvestDate')}
                  value={renderEstimateDate(data?.firstEstimatedHarvestDate)}
                />
              </Col>
            </Row>
          </Card>
          <Card className="farmer-card">
            <Row>
              <Col span={24}>
                <Typography.Title level={5}>
                  {t('addFgForm.plantingSeason2')}
                </Typography.Title>
              </Col>
              <Col span={12}>
                <TaskInfo
                  wrapperClass="no-padding"
                  header={t('addFgForm.estimatedPlantingDate')}
                  value={renderEstimateDate(data?.secondEstimatedStartDate)}
                />
              </Col>
              <Col span={12}>
                <TaskInfo
                  wrapperClass="no-padding"
                  header={t('addFgForm.estimatedHarvestDate')}
                  value={renderEstimateDate(data?.secondEstimatedHarvestDate)}
                />
              </Col>
            </Row>
          </Card>
          {data?.thirdEstimatedStartDate && data?.thirdEstimatedHarvestDate && (
            <Card className="farmer-card">
              <Row>
                <Col span={24}>
                  <Typography.Title level={5}>
                    {t('addFgForm.plantingSeason3')}
                  </Typography.Title>
                </Col>
                <Col span={12}>
                  <TaskInfo
                    wrapperClass="no-padding"
                    header={t('addFgForm.estimatedPlantingDate')}
                    value={renderEstimateDate(data?.thirdEstimatedStartDate)}
                  />
                </Col>
                <Col span={12}>
                  <TaskInfo
                    wrapperClass="no-padding"
                    header={t('addFgForm.estimatedHarvestDate')}
                    value={renderEstimateDate(data?.thirdEstimatedHarvestDate)}
                  />
                </Col>
              </Row>
            </Card>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Tabs
        items={tabItems}
        activeKey={activeKey}
        onTabClick={setActiveKey}
        defaultActiveKey="1"
      />
    </>
  );
};

export default FarmerGroupContent;
